// Composition root of the PWA — the web counterpart of TrailBivvy/App/AppEnvironment.swift. One instance, created in
// main.tsx and passed down through context; it owns the services and the app-wide state signals. Screens read
// signals and call methods here; features never reference each other's screens (iOS "no feature-to-feature links").

import { computed, effect, signal } from '@preact/signals';
import { createContext } from 'preact';
import { useContext } from 'preact/hooks';
import type { GeoBounds, GeoCoordinate, PointOfInterest, TrackPoint } from '../core/models';
import { t } from '../i18n';
import { distance as formatDistance } from '../i18n/format';
import { BatteryMonitor, BatteryPolicy, LocationPolicy } from '../location/locationPolicy';
import { BrowserLocationProvider } from '../location/browserLocationProvider';
import { isAuthorizationGranted, type LocationTrackingMode } from '../location/locationTrackingMode';
import type { LocationProvider } from '../location/locationProvider';
import { ConnectivityMonitor } from '../net/connectivityMonitor';
import { FetchHTTPClient } from '../net/fetchHTTPClient';
import { kvStore } from '../storage/idb';
import { prefs } from '../storage/prefs';
import { subscriptionApi } from '../store/api';
import { EntitlementManager, type StoredEntitlement } from '../store/entitlement';
import { trustedIssuers, trustedKeys } from '../store/keys';
import { activityHolds, holdAfterAccessChange, paywallPhase } from '../store/paywall';
import { makeWeatherService } from '../weather/weatherSetup';
import { MapProviderRegistry } from '../map/MapProviderRegistry';
import type { CompositeMapProvider } from '../map/CompositeMapProvider';
import { OfflineController, type OfflineControllerState } from '../offline/OfflineController';
import { OfflineDownloadManager } from '../offline/OfflineDownloadManager';
import { OfflineStore } from '../offline/OfflineStore';
import { loadDataset } from '../data/catalog';
import { coordinateAtDistance, cumulativeDistances } from '../core/geo';
import type { TrailDataset, TrailRoute } from '../core/models';
import { WeatherController } from '../weather/weatherController';
import type { WeatherResult } from '../weather/weatherService';
import { DataController } from '../app/dataController';
import { PlannerClient } from '../app/plannerClient';
import { offlineTileZoom } from '../app/plannerCore';
import { LaunchDestinationPolicy } from '../app/policies';
import { ReportRepository, WaypointRepository } from '../app/repositories';
import { RouteController } from '../app/routeController';
import { navigate, route } from './router';

/** App-wide sheets (iOS AppSheet): features ask for them here instead of referencing each other's views. */
export type AppSheet =
  | { kind: 'downloads'; stageIDs?: string[] }
  | { kind: 'waypointEditor'; coordinate?: GeoCoordinate; elevation?: number; waypointID?: string }
  | { kind: 'poiDetail'; poi: PointOfInterest }
  | { kind: 'spotCheck'; coordinate: GeoCoordinate }
  | { kind: 'responsibleBivouac' }
  | { kind: 'sources' }
  | { kind: 'regulations' };

/** Where Sleep Tonight plans from (iOS TonightOrigin). */
export type TonightOrigin =
  | { kind: 'currentLocation' }
  | { kind: 'stageStart'; stageID: string }
  | { kind: 'coordinate'; coordinate: GeoCoordinate; elevation?: number };

/** A request for the Map to show something (iOS MapFocusRequest). */
export type MapFocusRequest =
  | { kind: 'stage'; stageID: string }
  | { kind: 'trail'; trailID: string }
  | { kind: 'coordinate'; coordinate: GeoCoordinate; zoom?: number }
  | { kind: 'poi'; poi: PointOfInterest }
  | { kind: 'route' }
  /** Frame an area (a search area, an overnight option's surroundings). */
  | { kind: 'bounds'; bounds: GeoBounds }
  /** Draw and frame a saved track (trip history). */
  | { kind: 'track'; coordinates: GeoCoordinate[] };

/** Feature flags (iOS FeatureFlags): production always has the paywall on; dev builds may switch it off. */
function paywallEnabled(): boolean {
  if (!import.meta.env.DEV) return true;
  try {
    return localStorage.getItem('tb.flags.paywallEnabled') !== 'false';
  } catch {
    return true;
  }
}

function foregroundModeFor(mode: typeof prefs.gpsPowerMode.value): LocationTrackingMode {
  return mode === 'batterySaver' ? 'batterySaver' : mode === 'precise' ? 'navigation' : 'standard';
}

export class AppEnv {
  readonly prefs = prefs;
  readonly now = () => Date.now();

  // MARK: Subscription
  readonly entitlement = new EntitlementManager({
    store: kvStore<StoredEntitlement>('entitlement'),
    api: subscriptionApi,
    trustedKeys,
    trustedIssuers,
    isOnline: () => this.isOnline.value,
  });
  readonly paywallEnabled = paywallEnabled();

  // MARK: Connectivity (iOS ConnectivityMonitor; forceOffline = manual offline mode)
  readonly connectivity = new ConnectivityMonitor();
  readonly isOnline = signal(true);

  // MARK: Maps and offline downloads
  readonly mapProvider: CompositeMapProvider;
  readonly offlineStore = new OfflineStore();
  readonly offline: OfflineController;
  readonly offlineState = signal<OfflineControllerState>({ regions: [], diskUsage: 0 });

  // MARK: Data, route, activities
  readonly data = new DataController({ storedTrailID: prefs.activeTrailID, offlineDatasets: () => this.offlineDatasets() });
  readonly routes: RouteController;
  readonly waypoints = new WaypointRepository();
  readonly reports = new ReportRepository();
  readonly planner = new PlannerClient();

  // MARK: Location
  readonly location: LocationProvider = new BrowserLocationProvider();
  readonly locationAuthorization = signal(this.location.authorization);
  readonly lastFix = signal<TrackPoint | undefined>(undefined);
  readonly heading = signal<number | undefined>(undefined);
  /** Screens that need the position while visible (Map follow, Tonight from my position, Safety). */
  readonly locationDemand = signal(0);
  readonly isVisible = signal(typeof document === 'undefined' ? true : document.visibilityState === 'visible');
  private readonly battery = new BatteryMonitor();
  private readonly batteryTick = signal(0);

  // MARK: Weather
  readonly weather = new WeatherController({ service: makeWeatherService({ connectivity: this.connectivity }), now: this.now });
  readonly weatherCurrent = signal<WeatherResult | undefined>(undefined);
  readonly weatherLoading = signal(false);

  // MARK: Routing state
  readonly sheet = signal<AppSheet | undefined>(undefined);
  readonly tonightOrigin = signal<TonightOrigin>({ kind: 'currentLocation' });
  readonly mapFocusRequest = signal<MapFocusRequest | undefined>(undefined);
  readonly safetyOpen = signal(false);
  readonly toast = signal<string | undefined>(undefined);
  /** The install prompt captured from the browser (Chromium), for More › Install. */
  readonly installPrompt = signal<(Event & { prompt(): Promise<void> }) | undefined>(undefined);
  private toastTimer: ReturnType<typeof setTimeout> | undefined;

  // MARK: Paywall (iOS PaywallPolicy)
  readonly liveActivity: ReturnType<typeof computed<boolean>>;
  readonly paywallHoldUntil = signal<number | undefined>(undefined);
  readonly clockTick = signal(Date.now());
  readonly paywall = computed(() => {
    if (!this.paywallEnabled) return undefined;
    this.clockTick.value;
    const holds = activityHolds(this.liveActivity.value, this.paywallHoldUntil.value, Date.now());
    return paywallPhase(this.entitlement.access.value, prefs.onboardingCompleted.value, holds);
  });

  private wakeLock: { release(): Promise<void> } | undefined;
  /** The screen wake lock is actually held (some browsers refuse it, e.g. in battery saver). */
  readonly wakeLockHeld = signal(false);

  constructor() {
    this.routes = new RouteController(this.data, prefs, this.now, {
      leftRoute: (distance) => this.alertOffRoute(distance),
    });
    this.liveActivity = this.routes.liveActivity;

    // Maps (official providers per region, OSM fallback) and offline downloads (iOS OfflineController).
    this.mapProvider = MapProviderRegistry.standard({
      client: new FetchHTTPClient({ connectivity: this.connectivity }),
      connectivity: this.connectivity,
      localProtectedAreas: async (bounds) => this.data.dataStore.peek().protectedAreasIn(bounds),
    });
    this.offline = new OfflineController({
      store: this.offlineStore,
      manager: new OfflineDownloadManager({ store: this.offlineStore, connectivity: this.connectivity }),
      mapProvider: this.mapProvider,
    });
    this.offline.subscribe((state) => {
      this.offlineState.value = state;
      void this.updateOfflineCoverage();
    });
    this.offline.onDatasetsChanged = () => void this.data.reloadData();
    this.offline.prefetchWeather = (r) => this.prefetchWeather(r);

    // Connectivity, with the manual offline mode.
    this.connectivity.start();
    this.connectivity.forceOffline = prefs.manualOfflineMode.value;
    this.isOnline.value = this.connectivity.isOnline;
    this.connectivity.subscribe((m) => {
      const wasOnline = this.isOnline.value;
      this.isOnline.value = m.isOnline;
      if (!wasOnline && m.isOnline) this.entitlement.onResume();
    });
    effect(() => {
      this.connectivity.forceOffline = prefs.manualOfflineMode.value;
      this.isOnline.value = this.connectivity.isOnline;
    });

    // Location fixes feed navigation and recording (ARCHITECTURE.md §4.1).
    this.location.subscribe((fix) => {
      this.lastFix.value = fix;
      this.routes.navigation.handle(fix);
      this.routes.recorder.handle(fix);
    });
    this.location.subscribeState((p) => {
      this.locationAuthorization.value = p.authorization;
      this.heading.value = p.heading;
    });
    effect(() => this.updateLocationMode());
    void this.battery.start().then(() => this.battery.subscribe(() => (this.batteryTick.value += 1)));

    // Weather results as signals.
    this.weather.subscribe((c) => {
      this.weatherCurrent.value = c.current;
      this.weatherLoading.value = c.isLoading;
    });

    // The planner worker follows the data.
    effect(() => {
      this.data.generation.value;
      this.planner.setData(this.data.dataStore.peek().dataset);
    });

    // Paywall hold when access ends during an activity; activities suspended when the paywall locks (iOS S4).
    let previousAccess = this.entitlement.access.value;
    this.entitlement.access.subscribe((access) => {
      this.paywallHoldUntil.value = holdAfterAccessChange(previousAccess, access, this.liveActivity.value, this.paywallHoldUntil.value, Date.now());
      previousAccess = access;
    });
    effect(() => {
      if (this.paywall.value === 'locked') {
        const recorder = this.routes.recorder;
        if (recorder.isRecording.peek() && !recorder.isPaused.peek()) recorder.pause();
        if (this.routes.navigation.isActive.peek()) this.routes.stopNavigation();
      }
    });

    // Keep the screen on during navigation and recording: browsers stop geolocation when it locks (PWA-31).
    effect(() => {
      const wanted = this.liveActivity.value && this.isVisible.value;
      void this.setWakeLock(wanted);
    });

    if (typeof window !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        const visible = document.visibilityState === 'visible';
        this.isVisible.value = visible;
        if (visible) {
          this.clockTick.value = Date.now();
          this.entitlement.onResume();
        } else {
          void this.routes.recorder.flush();
          this.routes.markNavigationIfRunning();
        }
      });
      window.addEventListener('pagehide', () => void this.routes.recorder.flush());
      window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        this.installPrompt.value = e as Event & { prompt(): Promise<void> };
      });
      setInterval(() => (this.clockTick.value = Date.now()), 60_000);
    }
  }

  async start(): Promise<void> {
    const interrupted = LaunchDestinationPolicy.navigationWasInterrupted(prefs.navigationRunningAt.value, this.now());
    prefs.navigationRunningAt.value = undefined; // read once (iOS consumeInterruptedNavigation)
    await Promise.all([this.entitlement.start(), this.routes.start(), this.offline.start()]);
    await this.data.start();
    // Every launch opens on the trail list, except for a hiker out on the trail (iOS LA-1…LA-12).
    if (route.peek().path.length === 0) {
      const destination = LaunchDestinationPolicy.destination({
        isNavigating: this.routes.navigation.isActive.peek(),
        hasRecording: this.routes.recorder.isRecording.peek(),
        navigationInterrupted: interrupted,
      });
      navigate(destination === 'map' ? 'map' : 'route/catalog', { replace: true });
    }
  }

  /** Trail packs of completed downloads, merged into the live store (iOS: OfflineStore.allDatasets()). */
  private async offlineDatasets(): Promise<TrailDataset[]> {
    const out: TrailDataset[] = [];
    const seen = new Set<string>();
    for (const record of await this.offlineStore.records().catch(() => [])) {
      if (record.region.status !== 'completed') continue;
      for (const url of record.dataURLs) {
        const resource = /\/data\/trails\/([^/]+)\.json$/.exec(new URL(url, location.origin).pathname)?.[1];
        if (!resource || seen.has(resource)) continue;
        seen.add(resource);
        const entry = this.data.catalog.peek().find((e) => e.packResource === resource);
        const dataset = entry ? await loadDataset(entry).catch(() => undefined) : undefined;
        if (dataset) out.push(dataset);
      }
    }
    return out;
  }

  /** Downloaded map coverage for Sleep Tonight's "available offline" (iOS TonightOfflineMaps), as zoom-14 tile keys. */
  private async updateOfflineCoverage(): Promise<void> {
    const keys = new Set<string>();
    for (const record of await this.offlineStore.records().catch(() => [])) {
      if (record.region.status !== 'completed' || !record.plan) continue;
      for (const flat of Object.values(record.plan.tiles)) {
        for (let i = 0; i + 2 < flat.length; i += 3) {
          if (flat[i] === offlineTileZoom) keys.add(`${flat[i + 1]}/${flat[i + 2]}`);
        }
      }
    }
    this.planner.setOfflineTiles([...keys]);
  }

  /** "All offline data" also caches forecasts along the route: one point every ~8 km, at most 40 (iOS). */
  private async prefetchWeather(r: TrailRoute): Promise<void> {
    if (!this.isOnline.peek()) return;
    const points = r.polyline.points;
    if (points.length === 0) return;
    const cumulative = cumulativeDistances(r.polyline);
    const total = cumulative[cumulative.length - 1] ?? 0;
    const step = Math.max(8_000, total / 40);
    const coords = points.map((p) => p.coordinate);
    for (let d = 0; d <= total; d += step) {
      const c = coordinateAtDistance(d, coords, cumulative);
      if (!c) continue;
      let i = 0;
      while (i < cumulative.length - 1 && cumulative[i] < d) i++;
      await this.weather.forecastFor(c, points[i]?.elevation).catch(() => undefined);
    }
  }

  // MARK: Location

  /** Asks the browser for the position now (a user action: Locate me, Use my position, Safety). */
  requestLocation(): void {
    this.location.requestAuthorization();
    this.locationDemand.value += 1;
    setTimeout(() => (this.locationDemand.value = Math.max(0, this.locationDemand.value - 1)), 60_000);
  }

  /** A screen that shows the position registers while it is on screen; returns the release function. */
  useLocationWhileVisible(): () => void {
    this.locationDemand.value += 1;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.locationDemand.value = Math.max(0, this.locationDemand.value - 1);
    };
  }

  /** iOS updateLocationMode: the least power-hungry mode that satisfies what the hiker is doing. */
  private updateLocationMode(): void {
    const recorder = this.routes.recorder;
    const granted = isAuthorizationGranted(this.locationAuthorization.value);
    const wantsForeground = this.locationDemand.value > 0 && (granted || this.locationAuthorization.value === 'notDetermined' || this.locationAuthorization.value === 'unknown');
    const plan = LocationPolicy.plan({
      isRecording: recorder.isRecording.value && !recorder.isPaused.value,
      isNavigating: this.routes.navigation.isActive.value,
      screenOffAlertsAvailable: false,
      isInForeground: this.isVisible.value,
      hasBeenActive: true,
      hasFullAccuracy: true,
      foregroundMode: wantsForeground ? foregroundModeFor(prefs.gpsPowerMode.value) : 'off',
    });
    this.batteryTick.value;
    const battery = this.battery.state;
    const mode = BatteryPolicy.recommendedMode(plan.mode, battery.level, battery.isCharging, battery.lowPowerMode);
    this.location.setMode(mode, { background: plan.background, heading: plan.heading });
  }

  // MARK: Activities

  startNavigation(confirmed = false): boolean {
    this.location.requestAuthorization();
    return this.routes.startNavigation(confirmed);
  }

  stopNavigation(): void {
    this.routes.stopNavigation();
  }

  startRecording(): void {
    this.location.requestAuthorization();
    this.routes.startRecording(t('common.sleepTonight'));
  }

  private async setWakeLock(on: boolean): Promise<void> {
    const nav = navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> } };
    try {
      if (on && !this.wakeLock && nav.wakeLock) {
        const lock = (await nav.wakeLock.request('screen')) as { release(): Promise<void>; addEventListener?(t: string, f: () => void): void };
        this.wakeLock = lock;
        lock.addEventListener?.('release', () => {
          this.wakeLock = undefined;
          this.wakeLockHeld.value = false;
        });
      }
      if (!on && this.wakeLock) {
        await this.wakeLock.release();
        this.wakeLock = undefined;
      }
    } catch {
      this.wakeLock = undefined; // denied (battery saver) or unsupported: navigation still works while the screen is on
    }
    this.wakeLockHeld.value = this.wakeLock !== undefined;
  }

  /** Off-route alert (iOS: haptic + local notification when the app is not active). */
  private alertOffRoute(distance: number): void {
    try {
      navigator.vibrate?.([250, 120, 250]);
    } catch {
      // vibration unsupported
    }
    if (document.visibilityState !== 'visible' && 'Notification' in window && Notification.permission === 'granted') {
      const title = t('navigation.offRoute.title');
      const body = t('navigation.offRoute.body', formatDistance(distance));
      void navigator.serviceWorker?.getRegistration().then((reg) => {
        if (reg) void reg.showNotification(title, { body, tag: 'offroute' });
        else new Notification(title, { body, tag: 'offroute' });
      });
    }
  }

  // MARK: Routing helpers (iOS AppRouting)

  openSheet(sheet: AppSheet): void {
    this.sheet.value = sheet;
  }

  closeSheet(): void {
    this.sheet.value = undefined;
  }

  planTonight(origin: TonightOrigin): void {
    this.tonightOrigin.value = origin;
    navigate('tonight');
  }

  showStage(stageID: string): void {
    navigate(['route', 'stage', stageID]);
  }

  showOnMap(request: MapFocusRequest): void {
    this.mapFocusRequest.value = request;
    navigate('map');
  }

  openTrailList(): void {
    navigate('route/catalog');
  }

  showToast(message: string): void {
    this.toast.value = message;
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => (this.toast.value = undefined), 3500);
  }
}

export const EnvContext = createContext<AppEnv | undefined>(undefined);

export function useEnv(): AppEnv {
  const env = useContext(EnvContext);
  if (!env) throw new Error('AppEnv missing');
  return env;
}
